import { useMemo } from 'react';
import { ApolloQueryResult, WatchQueryFetchPolicy } from '@apollo/client';

import {
  CategoryRestrictions,
  DisplayCategoryFragment,
  GetDisplayCategoriesQuery,
  useGetDisplayCategoriesQuery,
} from '@ocx/graphql';
import { usePersistQueryResult } from '@ocx/utils';

import { filterCategoriesRestriction, sortDisplayCategoriesByNameAndRank } from './display-categories.utils';
import { setCachedDisplayCategories } from './offers.cache';

export type UseDisplayCategoriesFilter = {
  /** Retailer chain id from runtime config (e.g. `useConfiguration('chainId')` in the app). */
  chainId: string;
  /** `membership.fuelAndConvenienceProfile?.restrictionVerification`. */
  membershipRestrictionVerification?: CategoryRestrictions | null;
};

export type UseDisplayCategoriesParams = {
  filter: UseDisplayCategoriesFilter;
  fetchPolicy?: WatchQueryFetchPolicy;
  persistToStorage?: boolean;
};

export type UseDisplayCategoriesReturn = {
  data: DisplayCategoryFragment[];
  loading: boolean;
  refetch: () => Promise<ApolloQueryResult<GetDisplayCategoriesQuery>>;
};

export const useDisplayCategories = (params: UseDisplayCategoriesParams): UseDisplayCategoriesReturn => {
  const { filter, fetchPolicy = 'cache-and-network', persistToStorage = false } = params;
  const { chainId, membershipRestrictionVerification } = filter;

  const queryVariables = useMemo(() => ({ filter: { chainIdEquals: chainId } }), [chainId]);

  const { data, loading, refetch } = useGetDisplayCategoriesQuery({
    fetchPolicy,
    variables: queryVariables,
  });

  usePersistQueryResult({
    data,
    variables: queryVariables,
    persist: setCachedDisplayCategories,
    enabled: persistToStorage,
    loading,
  });

  const categories: DisplayCategoryFragment[] = useMemo(() => {
    if (!data) {
      return [];
    }
    const displayAllCategories: DisplayCategoryFragment[] = data.displayCategories.edges
      .map((edge) => edge.node)
      .sort(sortDisplayCategoriesByNameAndRank);

    return filterCategoriesRestriction(displayAllCategories, membershipRestrictionVerification);
  }, [data, membershipRestrictionVerification]);

  return { data: categories, loading, refetch };
};
